import React from "react";
import {useIntl, FormattedMessage} from "react-intl";
import {
    Loader2, CircleOff
} from "lucide-react";
import {getThemeColor} from "../../utils/topicUtils";
import {getSessionOutcome, isFeedbackOnly, sessionHasFeedback} from "./sessionUtils";
import {getContrastColors} from "../../UIElements";
import SessionMarker from "./SessionMarker";
import FeedbackTag from "./FeedbackTag";
import SupportRequestPill, {supportRequestState} from "./SupportRequestPill";

function formatSessionTime(dateStr, intl) {
    const date = new Date(dateStr)
    const now = new Date()
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86400000)
    const time = intl.formatTime(date, {hour: "2-digit", minute: "2-digit"})

    if (dayDiff === 0 || dayDiff === 1) {
        const rel = intl.formatRelativeTime(-dayDiff, "day", {numeric: "auto"})
        const word = rel.charAt(0).toUpperCase() + rel.slice(1)
        return `${word} ${time}`
    }

    const sameYear = date.getFullYear() === now.getFullYear()
    const datePart = intl.formatDate(date, {day: "numeric", month: "short", ...(sameYear ? {} : {year: "numeric"})})
    return `${datePart} ${time}`
}

function ThreadBadge({index, total, intl}) {
    if (!total || total <= 1) return null

    const title = intl.formatMessage(
        {id: "analysis.threadPosition", defaultMessage: "This student has {total} conversations — you're viewing number {position}"},
        {position: index + 1, total}
    )

    return <span className="thread-badge" title={title}>
        <FormattedMessage
            id="analysis.threadCount"
            defaultMessage="{position} of {total}"
            values={{position: index + 1, total}}
        />
    </span>
}

export default function SessionRow({
    session,
    contact,
    threadIndex,
    threadTotal,
    isSelected,
    onClick,
    onRequestAnalysis,
    pendingAnalysis,
    surveyNames,
    topicLabels,
    topicThemes,
    supportNeeds,
    needAttribution,
}) {
    const intl = useIntl()
    const {contactName} = contact
    const analysis = session.llmanalysis

    const {status, feedback} = getSessionOutcome(session)
    const feedbackOnly = isFeedbackOnly(session)
    const hasFeedbackTag = !feedbackOnly && sessionHasFeedback(session)

    const campaignName = session.survey ? (surveyNames?.[session.survey] || session.survey) : null
    const topicCodes = analysis?.topics || []

    const feedbackResponse = analysis?.feedback_response ?? null
    const feedbackDetails = analysis?.feedback_details || null
    const summaryText = analysis?.summary || null
    const showFeedbackTag = feedbackOnly || hasFeedbackTag
    const splitLayout = hasFeedbackTag && !!feedbackDetails && !!summaryText

    const hasSupportPill = !!supportRequestState(session, contact.contactId, needAttribution, supportNeeds).kind
    const supportPill = <SupportRequestPill session={session} contactId={contact.contactId} attribution={needAttribution} supportNeeds={supportNeeds} />
    // Single-block text: feedback-only rows show the note, everything else the summary.
    const singleText = feedbackOnly ? feedbackDetails : (summaryText || feedbackDetails)

    const messageCount = Array.isArray(session.messages) ? session.messages.length : 0
    const sessionTime = formatSessionTime(session.created || session.updated, intl)

    const handleAnalysisClick = (e) => {
        e.stopPropagation()
        if (!pendingAnalysis && onRequestAnalysis) {
            onRequestAnalysis(session.id)
        }
    }

    return <div
        className={`contact-row ${isSelected ? "selected" : ""}`}
        onClick={() => onClick(session, contact)}
    >
        <div className="col-info">
            <div className="col-indicator">
                <SessionMarker status={status} feedback={feedback} />
            </div>
            <div className={"col-contact"}>
                <div className="info-line-top">
                    <span className="contact-name">{contactName}</span>
                    <ThreadBadge index={threadIndex} total={threadTotal} intl={intl} />
                </div>
                {campaignName && <span className="contact-campaign">{campaignName}</span>}
                <div className="info-line-date">
                    <span className="session-time">{sessionTime}</span>
                    {messageCount > 0 && (
                        <>
                            <span className="meta-sep">·</span>
                            <span className="session-messages">
                            <FormattedMessage id="analysis.totalMessageCount" defaultMessage="{count, plural, one {# message} other {# messages}}" values={{count: messageCount}} />
                        </span>
                        </>
                    )}
                </div>
            </div>
        </div>

        <div className="col-summary">
            {splitLayout ? (
                <div className="contact-summary">
                    <div className="cs-feedback-line">
                        {supportPill}
                        <FeedbackTag response={feedbackResponse} />
                        <span className="cs-feedback-note">"{feedbackDetails}"</span>
                    </div>
                    <div className="cs-block cs-clamp-2">{summaryText}</div>
                </div>
            ) : (showFeedbackTag || singleText || hasSupportPill) ? (
                <div className="contact-summary">
                    <div className="cs-block cs-clamp-3">
                        {supportPill}
                        {showFeedbackTag && <FeedbackTag response={feedbackResponse} />}
                        {singleText}
                    </div>
                </div>
            ) : null}
            {!analysis && (
                <div className="contact-row-bottom">
                    <span className="not-analyzed">
                        <CircleOff size={14} />
                        <FormattedMessage id="analysis.notAnalyzed" defaultMessage="Not analyzed" />
                    </span>
                </div>
            )}
        </div>

        <div className="col-topics">
            {!analysis &&
                <button
                    className={`request-analysis-btn ${pendingAnalysis ? "pending" : ""}`}
                    onClick={handleAnalysisClick}
                    disabled={pendingAnalysis}
                >
                    {pendingAnalysis ? (
                        <><Loader2 size={12} className="spin" /> <FormattedMessage id="analysis.analyzing" defaultMessage="Analyzing..." /></>
                    ) : (
                        <FormattedMessage id="analysis.requestAnalysis" defaultMessage="Request analysis" />
                    )}
                </button>
            }
            {topicCodes.map(code => (
                <span
                    key={code}
                    className="topic-tag"
                    style={{backgroundColor: getThemeColor(topicThemes?.[code]), color: getContrastColors(getThemeColor(topicThemes?.[code])).textColor}}
                >
                    {topicLabels?.[code] || code}
                </span>
            ))}
        </div>
    </div>
}
